import React from 'react';
import { Link } from 'react-router-dom';
import {
    MapPin,
    Calendar,
    TrendingUp,
    DollarSign,
    Users,
    Clock,
    ArrowRight,
    Star,
    Building2,
    Leaf,
    Sun,
    Target
} from 'lucide-react';
import { Project } from '../../store/slices/apiSlice';

interface ProjectCardProps {
    project: Project;
    showCategory?: boolean;
    className?: string;
}

const ProjectCard: React.FC<ProjectCardProps> = ({
    project,
    showCategory = true,
    className = ''
}) => {
    // Helper functions to replace projectService methods
    const calculateProgress = (goal: number, raised: number | null) => {
        if (!raised || goal === 0) return 0;
        return Math.min((raised / goal) * 100, 100);
    };

    const getDaysUntilDeadline = (deadline: string) => {
        const deadlineDate = new Date(deadline);
        const today = new Date();
        const diffTime = deadlineDate.getTime() - today.getTime();
        const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
        return diffDays > 0 ? diffDays : 0;
    };

    const isFeaturedProject = (project: Project) => {
        return project.status === 'featured' || project.featured === true;
    };

    const formatCurrency = (amount: number, currencySymbol: string = '$') => {
        return new Intl.NumberFormat('en-US', {
            style: 'currency',
            currency: 'USD',
            maximumFractionDigits: 0
        }).format(amount).replace('$', currencySymbol);
    };

    const formatROI = (roi: string) => {
        return `${parseFloat(roi).toFixed(1)}%`;
    };

    const formatDuration = (months: number) => {
        if (months < 12) {
            return `${months} months`;
        } else {
            const years = Math.floor(months / 12);
            const remainingMonths = months % 12;
            return remainingMonths > 0 ? `${years}y ${remainingMonths}m` : `${years} years`;
        }
    };

    const progress = calculateProgress(project.funding_goal, project.amount_raised);
    const daysLeft = getDaysUntilDeadline(project.deadline);
    const isFeatured = isFeaturedProject(project);

    // Get category icon
    const getCategoryIcon = (categoryName: string) => {
        switch (categoryName.toLowerCase()) {
            case 'real estate':
                return <Building2 className="w-4 h-4" />;
            case 'solar project':
                return <Sun className="w-4 h-4" />;
            case 'agriculture':
                return <Leaf className="w-4 h-4" />;
            default:
                return <Target className="w-4 h-4" />;
        }
    };

    // Get category color
    const getCategoryColor = (categoryName: string) => {
        switch (categoryName.toLowerCase()) {
            case 'real estate':
                return 'from-blue-500 to-blue-600';
            case 'solar project':
                return 'from-yellow-500 to-orange-600';
            case 'agriculture':
                return 'from-green-500 to-green-600';
            default:
                return 'from-purple-500 to-purple-600';
        }
    };

    return (
        <div className={`bg-white rounded-2xl shadow-lg hover:shadow-2xl transition-all duration-500 overflow-hidden group border border-gray-100 ${className}`}>
            {/* Project Image */}
            <div className="relative h-48 bg-gradient-to-br from-gray-100 to-gray-200 overflow-hidden">
                {project.images && project.images.length > 0 ? (
                    <img
                        src={project.images[0].image_url}
                        alt={project.title}
                        className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                    />
                ) : (
                    <div className="w-full h-full flex items-center justify-center">
                        <Building2 className="w-16 h-16 text-gray-400" />
                    </div>
                )}

                {/* Featured Badge */}
                {isFeatured && (
                    <div className="absolute top-4 left-4">
                        <div className="bg-gradient-to-r from-yellow-400 to-orange-500 text-white px-3 py-1 rounded-full text-sm font-semibold flex items-center gap-1">
                            <Star className="w-3 h-3" />
                            Featured
                        </div>
                    </div>
                )}

                {/* Category Badge */}
                {showCategory && (
                    <div className="absolute top-4 right-4">
                        <div className={`bg-gradient-to-r ${getCategoryColor(project.category.name)} text-white px-3 py-1 rounded-full text-sm font-semibold flex items-center gap-1`}>
                            {getCategoryIcon(project.category.name)}
                            {project.category.name}
                        </div>
                    </div>
                )}

                {/* Progress Bar */}
                <div className="absolute bottom-0 left-0 right-0 bg-black/60 backdrop-blur-md p-4 transition-all duration-300 group-hover:bg-black/70">
                    <div className="flex justify-between items-center text-white text-sm font-semibold mb-2 drop-shadow-sm">
                        <span>Funding Progress</span>
                        <span className="bg-white/20 px-2 py-0.5 rounded-md backdrop-blur-sm">{progress.toFixed(1)}%</span>
                    </div>
                    <div className="w-full bg-white/20 rounded-full h-2.5 overflow-hidden">
                        <div
                            className="bg-gradient-to-r from-green-400 to-green-500 h-full rounded-full transition-all duration-1000 ease-out shadow-[0_0_10px_rgba(74,222,128,0.5)]"
                            style={{ width: `${progress}%` }}
                        ></div>
                    </div>
                </div>
            </div>

            {/* Project Content */}
            <div className="p-6">
                {/* Project Title */}
                <h3 className="text-xl font-bold text-gray-900 mb-3 group-hover:text-blue-600 transition-colors duration-300 line-clamp-2">
                    {project.title}
                </h3>

                {/* Project Description */}
                <p className="text-gray-600 mb-4 leading-relaxed line-clamp-3">
                    {project.short_description}
                </p>

                {/* Project Stats */}
                <div className="grid grid-cols-2 gap-4 mb-6">
                    <div className="flex items-center gap-2">
                        <TrendingUp className="w-4 h-4 text-green-500" />
                        <div>
                            <div className="text-sm text-gray-500">ROI</div>
                            <div className="font-semibold text-gray-900">
                                {formatROI(project.expected_roi_percentage)}
                            </div>
                        </div>
                    </div>
                    <div className="flex items-center gap-2">
                        <Clock className="w-4 h-4 text-blue-500" />
                        <div>
                            <div className="text-sm text-gray-500">Duration</div>
                            <div className="font-semibold text-gray-900">
                                {formatDuration(project.roi_duration_months)}
                            </div>
                        </div>
                    </div>
                    <div className="flex items-center gap-2">
                        <DollarSign className="w-4 h-4 text-purple-500" />
                        <div>
                            <div className="text-sm text-gray-500">Goal</div>
                            <div className="font-semibold text-gray-900">
                                {formatCurrency(project.funding_goal, project.currency_symbol)}
                            </div>
                        </div>
                    </div>
                    <div className="flex items-center gap-2">
                        <Calendar className="w-4 h-4 text-orange-500" />
                        <div>
                            <div className="text-sm text-gray-500">Days Left</div>
                            <div className="font-semibold text-gray-900">
                                {daysLeft}
                            </div>
                        </div>
                    </div>
                </div>

                {/* Location */}
                <div className="flex items-center gap-2 mb-6 text-gray-600">
                    <MapPin className="w-4 h-4" />
                    <span className="text-sm line-clamp-1">{project.location}</span>
                </div>

                {/* Action Button */}
                <Link
                    to={`/projects/${project.project_id}`}
                    state={{ project }}
                    className={`block w-full text-center py-3 bg-gradient-to-r ${getCategoryColor(project.category.name)} text-white rounded-xl font-semibold hover:shadow-lg transition-all duration-300 hover:-translate-y-1`}
                >
                    View Details
                    <ArrowRight className="w-4 h-4 inline ml-2" />
                </Link>
            </div>
        </div>
    );
};

export default ProjectCard;
